import React, { useCallback, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useSelector } from 'react-redux';

import { tunnelSelectors, userSelectors } from 'reducers';
import { fundTypes } from 'utils/constants';
import { parseFullName } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { ActionButton, CancelButton, CheckboxLabel, CustomDialogTitle } from 'components';
import { Dialog, DialogActions, DialogContent } from '@mui/material';
import { titles } from 'utils/formData';

const checkboxSx = {
    display: 'table',
    alignItems: 'baseline',

    '& .MuiFormControlLabel-label': {
        display: 'table-cell',
    },
};

const MandatoryFormDialog = ({ open, onClose, onSubmit }) => {
    const [form, setForm] = useState({ box1: false, box2: false, box3: false });

    const handleChange = useCallback(
        key => e => setForm(prevState => ({ ...prevState, [key]: e.target.checked })),
        []
    );
    const fund = useSelector(tunnelSelectors.getCurrentInvestmentFund);
    const label2TextKey = useMemo(() => `BS_CHECKBOX_LABEL_2_${fund?.type.toUpperCase()}`, [fund]);
    const label3TextKey = useMemo(() => `BS_CHECKBOX_LABEL_3_${fund?.type.toUpperCase()}`, [fund]);
    const internalUser = useSelector(userSelectors.getInternalUser);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const subcategory = tunnelSubscription?.investmentCharacteristics?.shareSubcategory;

    const subscriberGender = useMemo(
        () => (internalUser?.title === titles[0] ? 'm' : 'f'),
        [internalUser?.title]
    );
    const coSubscriberGender = useMemo(() => {
        if (tunnelSubscription.withCoSubscriber) {
            return tunnelSubscription.coSubscriber?.title === titles[0] ? 'm' : 'f';
        }
        return '';
    }, [tunnelSubscription]);

    const showBox3 = useMemo(
        () =>
            tunnelSubscription?.withCoSubscriber &&
            fund?.type !== fundTypes.SLP &&
            fund?.type !== fundTypes.FPS,
        [fund?.type, tunnelSubscription?.withCoSubscriber]
    );

    return (
        <Dialog open={open} maxWidth="xl">
            <CustomDialogTitle title={texts.MANDATORY_FORM} handleClose={onClose} />
            <DialogContent>
                <CheckboxLabel
                    sx={checkboxSx}
                    onChange={handleChange('box1')}
                    checked={form.box1}
                    label={texts.BS_CHECKBOX_LABEL_1}
                />
                <ul>
                    <li>{texts.BS_CHECKBOX_LABEL_1_PARAGRAPH_1}</li>
                    <li>{texts.BS_CHECKBOX_LABEL_1_PARAGRAPH_2}</li>
                    <li>{texts.BS_CHECKBOX_LABEL_1_PARAGRAPH_3}</li>
                    <li>{texts.BS_CHECKBOX_LABEL_1_PARAGRAPH_4}</li>
                    <li>{texts.BS_CHECKBOX_LABEL_1_PARAGRAPH_5}</li>
                </ul>
                <CheckboxLabel
                    sx={checkboxSx}
                    onChange={handleChange('box2')}
                    checked={form.box2}
                    label={
                        fund?.type === fundTypes.SLP
                            ? texts[label2TextKey]
                            : texts[label2TextKey](
                                  subcategory,
                                  parseFullName(internalUser),
                                  subscriberGender
                              )
                    }
                />
                {showBox3 && (
                    <CheckboxLabel
                        sx={checkboxSx}
                        onChange={handleChange('box3')}
                        checked={form.box3}
                        label={texts[label3TextKey](
                            subcategory,
                            parseFullName(tunnelSubscription.coSubscriber),
                            coSubscriberGender
                        )}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={onClose}>{texts.CANCEL}</CancelButton>
                <ActionButton
                    disabled={!form.box1 || !form.box2 || (!form.box3 && showBox3)}
                    onClick={onSubmit}
                >
                    {texts.CONTINUER}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

MandatoryFormDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
};

export default MandatoryFormDialog;
